import React from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Input, Select, Button, FileInput } from '../../components/ui';
import useStyles from '../../theme/useStyles';
import {
  GENDERS, RELATIONS, MARITAL_STATUSES, BLOOD_GROUPS, LIFE_STATUSES, COMMUNION_STATUSES
} from '../members/congregationSheet';
import { addMonths, DEFAULT_GUEST_MONTHS, GUEST_STATUS } from '../members/guest';
import { todayISO } from '../../utils/format';
import type { RegistrationFormData, RegistrationPerson } from '../../api/endpoints';

// The household form shared by the public registration page and the office's
// review. Every person card carries the same sections and fields as the
// office's Add Head / Add Member form (the photo is hidden there, so here too);
// the head's card adds the family's address. Rules follow validateMember.ts.

export const MAX_MEMBERS = 20;

/** A document the server kept but did not send back (see KEEP in the service). */
const KEEP = 'KEEP';
const isMaskedAadhaar = (v?: string) => /^X{8}\d{4}$/.test(v || '');

const DOCUMENTS = [
  { key: 'baptismCert', label: 'Baptism Certificate' },
  { key: 'marriageCert', label: 'Marriage Certificate' },
  { key: 'membershipCert', label: 'Membership Certificate' },
  { key: 'idProof', label: 'ID Proof (Aadhaar/PAN)' }
] as const;
type DocKey = (typeof DOCUMENTS)[number]['key'];

/** The parish settings the form follows. */
export interface RegistrationOptions {
  memberStatuses: string[];
  documentUploads: boolean;
  guestMonths: number;
}

export const DEFAULT_OPTIONS: RegistrationOptions = {
  memberStatuses: ['Regular', 'Associate', 'NRI', 'Visitor', GUEST_STATUS],
  documentUploads: true,
  guestMonths: DEFAULT_GUEST_MONTHS
};

const newKey = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

export const emptyPerson = (relationToHead = ''): RegistrationPerson => ({
  key: newKey(), status: 'Alive', dateOfDeath: '', transferChurch: '', memberStatus: '', barcodeMembershipNo: '',
  guestFrom: '', guestUntil: '', guestOrigin: '', guestPurpose: '',
  name: '', relativeName: '', relationToHead, gender: '', location: '', dateOfBirth: '', maritalStatus: '',
  dateOfMarriage: '', bloodGroup: '', education: '', occupation: '', mobileNumber: '', aadhaarNumber: '',
  baptismDate: '', confirmationDate: '', communionStatus: ''
});

export const emptyForm = (mobile = '', dob = ''): RegistrationFormData => ({
  head: { ...emptyPerson('Head'), mobileNumber: mobile, dateOfBirth: dob },
  address: '', parish: '', email: '', members: []
});

/** Gives any person loaded without a key one, so edits can be matched on the server. */
export const withKeys = (form: RegistrationFormData): RegistrationFormData => ({
  ...form,
  head: { ...form.head, key: form.head.key || newKey() },
  members: (form.members || []).map((m) => ({ ...m, key: m.key || newKey() }))
});

const hasMarriageDate = (status?: string) => ['Married', 'Widowed', 'Divorced'].includes(status || '');
const isGuest = (p: RegistrationPerson) => (p.memberStatus || '').toLowerCase() === GUEST_STATUS.toLowerCase();

/**
 * The first problem a person would have to fix, or null — the member form's
 * rules. The server repeats every check; this only saves a round trip.
 */
export const validateRegistration = (form: RegistrationFormData): string | null => {
  const today = todayISO();
  const person = (p: RegistrationPerson, who: string, isHead: boolean): string | null => {
    const name = p.name.trim();
    if (!name) return `${who}: enter the full name.`;
    if (name.length < 3) return `${who}: enter the full name (at least 3 characters).`;
    if (!isHead && !p.relationToHead) return `${who}: choose the relation to the head.`;
    if (!p.gender) return `${who}: choose a gender.`;
    if (!p.dateOfBirth) return `${who}: enter the date of birth.`;
    if (p.dateOfBirth > today) return `${who}: the date of birth cannot be in the future.`;
    if (!p.maritalStatus) return `${who}: choose the marital status.`;
    if (isHead && !p.mobileNumber) return `${who}: enter the mobile number — it is the family contact.`;
    if (p.mobileNumber && !/^\d{7,12}$/.test(p.mobileNumber)) {
      return `${who}: enter a mobile number of 7 to 12 digits.`;
    }
    if (p.aadhaarNumber && !isMaskedAadhaar(p.aadhaarNumber) && !/^\d{12}$/.test(p.aadhaarNumber)) {
      return `${who}: the Aadhaar number must be exactly 12 digits.`;
    }
    const dob = p.dateOfBirth;
    if (p.status === 'Deceased') {
      if (!p.dateOfDeath) return `${who}: record the date of death.`;
      if (p.dateOfDeath < dob) return `${who}: the date of death cannot be before the date of birth.`;
    }
    if (p.status === 'Transferred' && !(p.transferChurch || '').trim()) return `${who}: record which church they moved to.`;
    if (isGuest(p)) {
      if (!p.guestFrom) return `${who}: record when the guest stay began.`;
      if (p.guestFrom < dob) return `${who}: the guest stay cannot begin before the date of birth.`;
      if (p.guestUntil && p.guestUntil <= p.guestFrom) return `${who}: the guest stay must end after it begins.`;
    }
    const dated: Array<[string | undefined, string]> = [
      [hasMarriageDate(p.maritalStatus) ? p.dateOfMarriage : '', 'date of marriage'],
      [p.baptismDate, 'baptism date'],
      [p.confirmationDate, 'confirmation date']
    ];
    for (const [value, label] of dated) {
      if (value && value < dob) return `${who}: the ${label} cannot be before the date of birth.`;
      if (value && value > today) return `${who}: the ${label} cannot be in the future.`;
    }
    if (p.baptismDate && p.confirmationDate && p.confirmationDate < p.baptismDate) {
      return `${who}: confirmation cannot be dated before baptism.`;
    }
    return null;
  };

  const h = form.head;
  const headProblem = person(h, 'Family head', true);
  if (headProblem) return headProblem;
  if (!form.address.trim()) return 'Enter the family address.';
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Enter a valid email address.';

  const aadhaars = new Set(h.aadhaarNumber ? [h.aadhaarNumber] : []);
  let spouses = 0;
  for (let i = 0; i < form.members.length; i += 1) {
    const m = form.members[i];
    const who = m.name.trim() || `Member ${i + 1}`;
    const problem = person(m, who, false);
    if (problem) return problem;
    if (h.maritalStatus === 'Single' && ['Spouse', 'Son', 'Daughter'].includes(m.relationToHead || '')) {
      return `The head is marked Single, so ${who} cannot be added as ${m.relationToHead}.`;
    }
    if (m.relationToHead === 'Spouse' && ++spouses > 1) return 'Only one spouse can be added.';
    // A family may share one phone, so a mobile may repeat within the form.
    if (m.aadhaarNumber) {
      if (aadhaars.has(m.aadhaarNumber)) return `${who}: this Aadhaar number is already entered for another person.`;
      aadhaars.add(m.aadhaarNumber);
    }
  }
  return null;
};

interface Props {
  value: RegistrationFormData;
  onChange: (next: RegistrationFormData) => void;
  options?: RegistrationOptions;
  disabled?: boolean;
  /** Where a file problem (too large, unreadable) is reported. */
  onFileError?: (message: string) => void;
  isDarkMode?: boolean;
}

const RegistrationFormFields = ({
  value, onChange, options = DEFAULT_OPTIONS, disabled, onFileError, isDarkMode
}: Props) => {
  const s = useStyles(isDarkMode);
  const t = s.t;
  const today = todayISO();

  const setHead = (patch: Partial<RegistrationPerson>) =>
    onChange({ ...value, head: { ...value.head, ...patch } });
  const setMember = (index: number, patch: Partial<RegistrationPerson>) =>
    onChange({ ...value, members: value.members.map((m, i) => (i === index ? { ...m, ...patch } : m)) });
  const addMember = () => onChange({ ...value, members: [...value.members, emptyPerson()] });
  const removeMember = (index: number) =>
    onChange({ ...value, members: value.members.filter((_, i) => i !== index) });

  // Wraps to one column on a phone, where most families will fill this in.
  const grid: CSSProperties = {
    display: 'grid', gap: '12px 16px', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))'
  };
  const card: CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: t.radius.xl, padding: '16px',
    backgroundColor: t.bgSubtle, marginBottom: '14px'
  };
  const heading = (text: ReactNode, extra?: ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
      <div style={{ fontWeight: t.fontWeight.black, color: t.textMain, fontSize: t.fontSize.lg }}>{text}</div>
      {extra}
    </div>
  );
  const section = (title: string) => (
    <div style={{
      margin: '16px 0 10px', paddingBottom: '4px', borderBottom: `1px solid ${t.border}`,
      color: t.textAccent, fontWeight: t.fontWeight.bold, fontSize: t.fontSize.md
    }}>
      {title}
    </div>
  );

  const documentField = (p: RegistrationPerson, set: (patch: Partial<RegistrationPerson>) => void, key: DocKey, label: string) => {
    const name = p[key] || '';
    const url = p[`${key}Url` as const] || '';
    const clear = () => set({ [key]: '', [`${key}Url`]: '' } as Partial<RegistrationPerson>);
    const kept = url === KEEP;
    // The office sees the file itself; the family's copy comes back only as its name.
    const viewable = !kept && (url.startsWith('data:') || url.startsWith('http'));
    const status = name && (
      <span style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: t.fontSize.sm, marginTop: '4px' }}>
        <span style={{ color: t.income, fontWeight: 'bold' }}>{kept ? 'Already uploaded' : 'Attached'}: {name}</span>
        {viewable && (
          <a href={url} download={name} target="_blank" rel="noreferrer" style={{ color: t.textAccent, fontWeight: 'bold' }}>
            👁️ View
          </a>
        )}
        {!disabled && (
          <button
            type="button" onClick={clear}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: t.danger, fontWeight: 'bold', fontSize: t.fontSize.sm }}
          >
            ✕ Remove
          </button>
        )}
      </span>
    );
    if (disabled) {
      return (
        <div key={key}>
          <div style={s.label}>{label}</div>
          {status || <span style={{ color: t.textFaint, fontSize: t.fontSize.sm }}>Not attached</span>}
        </div>
      );
    }
    return (
      <div key={key}>
        <FileInput
          label={label} isDarkMode={isDarkMode}
          hint="Image or PDF, up to 1.5 MB."
          onFile={(fileName, dataUrl) => set({ [key]: fileName, [`${key}Url`]: dataUrl } as Partial<RegistrationPerson>)}
          onError={onFileError}
        />
        {status}
      </div>
    );
  };

  const personFields = (p: RegistrationPerson, set: (patch: Partial<RegistrationPerson>) => void, isHead: boolean) => {
    const guest = isGuest(p);
    return (
      <>
        {/* Same order as the office's Add Head / Add Member form: family, who
            they are, contact, church membership, sacraments, documents. */}
        {section('Family')}
        <div style={grid}>
          {isHead ? (
            <>
              <Input label="Relation to Head" value="Head of the family" disabled isDarkMode={isDarkMode} onChange={() => {}} />
              <Input
                label="Family Address" required rows={2} value={value.address} disabled={disabled}
                placeholder="House no., street, area, city, PIN"
                hint="The home address of the whole family — every member shares it."
                isDarkMode={isDarkMode} maxLength={500} containerStyle={{ gridColumn: '1 / -1' }}
                onChange={(e) => onChange({ ...value, address: e.target.value })}
              />
              <Input
                label="Parish" placeholder="e.g. St. Thomas" value={value.parish || ''} disabled={disabled} isDarkMode={isDarkMode}
                maxLength={100} onChange={(e) => onChange({ ...value, parish: e.target.value })}
              />
              <Input
                label="Family Email" type="email" value={value.email || ''} disabled={disabled} isDarkMode={isDarkMode}
                maxLength={120} onChange={(e) => onChange({ ...value, email: e.target.value })}
              />
            </>
          ) : (
            <>
              <Select
                label="Relation to Head" required value={p.relationToHead} disabled={disabled} isDarkMode={isDarkMode}
                options={RELATIONS} onChange={(e) => set({ relationToHead: e.target.value })}
              />
              {/* A member lives at the family's address — shown from Step 1, not typed again. */}
              <div style={{
                gridColumn: '1 / -1', padding: '10px 14px', borderRadius: t.radius.lg,
                backgroundColor: t.bgMain, border: `1px solid ${t.border}`, fontSize: t.fontSize.md
              }}>
                <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, fontWeight: t.fontWeight.bold, marginBottom: '2px' }}>
                  🏠 Family Address (same as the head{value.head.name.trim() ? `, ${value.head.name.trim()}` : ''})
                </div>
                <div style={{ color: t.textMain, whiteSpace: 'pre-line' }}>
                  {value.address.trim() || <em style={{ color: t.textFaint }}>Enter the family address in Step 1</em>}
                  {value.parish && <span style={{ color: t.textMuted }}> · Parish: {value.parish}</span>}
                </div>
              </div>
            </>
          )}
        </div>

        {section('Personal Details')}
        <div style={grid}>
          <Input
            label="Full Name" required placeholder="e.g. John Samuel" value={p.name} disabled={disabled}
            isDarkMode={isDarkMode} maxLength={100} onChange={(e) => set({ name: e.target.value })}
          />
          <Input
            label="S/o, D/o, W/o" placeholder="Father's / husband's name" value={p.relativeName || ''}
            disabled={disabled} isDarkMode={isDarkMode} maxLength={100}
            onChange={(e) => set({ relativeName: e.target.value })}
          />
          <Select
            label="Gender" required value={p.gender} disabled={disabled} isDarkMode={isDarkMode}
            options={GENDERS} onChange={(e) => set({ gender: e.target.value })}
          />
          <Input
            label="Date of Birth" type="date" required value={p.dateOfBirth} disabled={disabled}
            isDarkMode={isDarkMode} max={today} onChange={(e) => set({ dateOfBirth: e.target.value })}
          />
          <Select
            label="Marital Status" required value={p.maritalStatus} disabled={disabled} isDarkMode={isDarkMode}
            options={MARITAL_STATUSES}
            onChange={(e) => set({
              maritalStatus: e.target.value,
              ...(hasMarriageDate(e.target.value) ? {} : { dateOfMarriage: '' })
            })}
          />
          {hasMarriageDate(p.maritalStatus) && (
            <Input
              label="Date of Marriage" type="date" value={p.dateOfMarriage || ''} max={today} disabled={disabled}
              isDarkMode={isDarkMode} onChange={(e) => set({ dateOfMarriage: e.target.value })}
            />
          )}
          <Select
            label="Blood Group" value={p.bloodGroup || ''} disabled={disabled} isDarkMode={isDarkMode}
            options={BLOOD_GROUPS} onChange={(e) => set({ bloodGroup: e.target.value })}
          />
          <Input
            label="Education" placeholder="e.g. B.Sc" value={p.education || ''} disabled={disabled}
            isDarkMode={isDarkMode} maxLength={100} onChange={(e) => set({ education: e.target.value })}
          />
          <Input
            label="Occupation" placeholder="e.g. Teacher" value={p.occupation || ''} disabled={disabled}
            isDarkMode={isDarkMode} maxLength={100} onChange={(e) => set({ occupation: e.target.value })}
          />
        </div>

        {section('Contact & Identity')}
        <div style={grid}>
          <Input
            label="Mobile Number" required={isHead} value={p.mobileNumber || ''} disabled={disabled}
            isDarkMode={isDarkMode} inputMode="numeric" placeholder="7 to 12 digits"
            hint={isHead ? 'Also used to check your registration status later.' : undefined}
            onChange={(e) => set({ mobileNumber: e.target.value.replace(/\D/g, '').slice(0, 12) })}
          />
          {isMaskedAadhaar(p.aadhaarNumber) ? (
            <div>
              <Input
                label="Aadhaar Number" value={`XXXX XXXX ${(p.aadhaarNumber || '').slice(8)}`} disabled
                isDarkMode={isDarkMode} onChange={() => {}}
                hint="Saved. Hidden for your privacy."
              />
              {!disabled && (
                <button
                  type="button" onClick={() => set({ aadhaarNumber: '' })}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: t.textAccent, fontWeight: 'bold', fontSize: t.fontSize.sm }}
                >
                  ✏️ Change
                </button>
              )}
            </div>
          ) : (
            <Input
              label="Aadhaar Number" value={p.aadhaarNumber || ''} disabled={disabled} isDarkMode={isDarkMode}
              inputMode="numeric" placeholder="12 digits"
              onChange={(e) => set({ aadhaarNumber: e.target.value.replace(/\D/g, '').slice(0, 12) })}
            />
          )}
          <Input
            label="Area / Locality" placeholder="e.g. Anna Nagar" value={p.location || ''} disabled={disabled}
            isDarkMode={isDarkMode} maxLength={100} onChange={(e) => set({ location: e.target.value })}
          />
        </div>

        {section('Church Membership')}
        <div style={grid}>
          <Select
            label={`Member Status (${GUEST_STATUS} = temporary stay)`} value={p.memberStatus || ''}
            options={options.memberStatuses} disabled={disabled} isDarkMode={isDarkMode}
            onChange={(e) => {
              const memberStatus = e.target.value;
              const patch: Partial<RegistrationPerson> = { memberStatus };
              // Becoming a guest starts the stay today, for the parish's
              // default period, unless dates were already entered.
              if (memberStatus.toLowerCase() === GUEST_STATUS.toLowerCase() && !p.guestFrom) {
                patch.guestFrom = today;
                if (!p.guestUntil) patch.guestUntil = addMonths(today, options.guestMonths);
              }
              set(patch);
            }}
          />
          <Input
            label="Membership No." placeholder="Leave blank if you don't have one"
            hint="Optional — the church gives one on approval if left empty."
            value={p.barcodeMembershipNo || ''} disabled={disabled} isDarkMode={isDarkMode} maxLength={50}
            onChange={(e) => set({ barcodeMembershipNo: e.target.value })}
          />
          {isHead ? (
            <Input label="Life Status" value="Alive" disabled isDarkMode={isDarkMode} onChange={() => {}} />
          ) : (
            <Select
              label="Life Status" placeholder="" options={LIFE_STATUSES.filter((s) => s !== 'Inactive')} value={p.status || 'Alive'}
              disabled={disabled} isDarkMode={isDarkMode}
              onChange={(e) => set({ status: e.target.value })}
            />
          )}
          {p.status === 'Deceased' && (
            <Input
              label="Date of Death" type="date" required value={p.dateOfDeath || ''} max={today}
              disabled={disabled} isDarkMode={isDarkMode}
              onChange={(e) => set({ dateOfDeath: e.target.value })}
            />
          )}
          {p.status === 'Transferred' && (
            <Input
              label="Transferred To (Church)" required placeholder="Name of receiving church"
              value={p.transferChurch || ''} disabled={disabled} isDarkMode={isDarkMode} maxLength={150}
              onChange={(e) => set({ transferChurch: e.target.value })}
            />
          )}
        </div>

        {guest && (
          <>
            {section('Guest Stay')}
            <div style={grid}>
              <Input
                label="Guest since" type="date" required value={p.guestFrom || ''} disabled={disabled} isDarkMode={isDarkMode}
                onChange={(e) => {
                  const from = e.target.value;
                  // Keep the default period unless the end was set by hand.
                  const untilIsDefault = !p.guestUntil || (p.guestFrom && p.guestUntil === addMonths(p.guestFrom, options.guestMonths));
                  set({ guestFrom: from, ...(untilIsDefault && from ? { guestUntil: addMonths(from, options.guestMonths) } : {}) });
                }}
              />
              <Input
                label="Expected until" type="date" value={p.guestUntil || ''} disabled={disabled} isDarkMode={isDarkMode}
                hint={`Leave blank for an open-ended stay. Default is ${options.guestMonths} months.`}
                onChange={(e) => set({ guestUntil: e.target.value })}
              />
              <Input
                label="Home church / came from" placeholder="e.g. CSI Church, Nagercoil" value={p.guestOrigin || ''}
                disabled={disabled} isDarkMode={isDarkMode} maxLength={150}
                onChange={(e) => set({ guestOrigin: e.target.value })}
              />
              <Input
                label="Purpose of stay" placeholder="e.g. College, work posting, staying with family" value={p.guestPurpose || ''}
                disabled={disabled} isDarkMode={isDarkMode} maxLength={150}
                onChange={(e) => set({ guestPurpose: e.target.value })}
              />
            </div>
          </>
        )}

        {section('Sacraments')}
        <div style={grid}>
          <Input
            label="Baptism Date" type="date" value={p.baptismDate || ''} max={today} disabled={disabled}
            isDarkMode={isDarkMode} onChange={(e) => set({ baptismDate: e.target.value })}
          />
          <Input
            label="Confirmation Date" type="date" value={p.confirmationDate || ''} max={today} disabled={disabled}
            isDarkMode={isDarkMode} onChange={(e) => set({ confirmationDate: e.target.value })}
          />
          <Select
            label="Communion Status" value={p.communionStatus || ''} disabled={disabled} isDarkMode={isDarkMode}
            options={COMMUNION_STATUSES} onChange={(e) => set({ communionStatus: e.target.value })}
          />
        </div>

        {options.documentUploads && (
          <>
            {section('Documents')}
            <div style={grid}>
              {DOCUMENTS.map((d) => documentField(p, set, d.key, d.label))}
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div>
      <div style={card}>
        {heading('👤 Step 1 · Family head')}
        {personFields(value.head, setHead, true)}
      </div>

      <div style={{ fontWeight: t.fontWeight.black, color: t.textMain, fontSize: t.fontSize.lg, margin: '20px 0 4px' }}>
        👥 Step 2 · Family members
      </div>
      <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: '0 0 12px' }}>
        Add everyone else in the household — spouse, children, parents. Skip this if the head lives alone.
      </p>

      {value.members.map((m, i) => (
        <div key={m.key || i} style={card}>
          {heading(
            `Member ${i + 1}${m.name.trim() ? ` · ${m.name.trim()}` : ''}`,
            !disabled && (
              <Button type="button" variant="ghost" size="sm" isDarkMode={isDarkMode} onClick={() => removeMember(i)}>
                ✕ Remove
              </Button>
            )
          )}
          {personFields(m, (patch) => setMember(i, patch), false)}
        </div>
      ))}

      {!disabled && value.members.length < MAX_MEMBERS && (
        <Button type="button" variant="secondary" isDarkMode={isDarkMode} onClick={addMember}>
          + Add family member
        </Button>
      )}
    </div>
  );
};

export default RegistrationFormFields;
